import './DropdownMenu.scss';
import cn from 'classnames';
import {
  ComponentProps,
  ReactElement,
  ReactNode,
  Ref,
  useCallback,
  useId,
  useMemo,
  useState
} from 'react';
import { Dropdown as BSDropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { MdCircle, MdOutlineCircle } from 'react-icons/md';
import { RiArrowUpSFill, RiFilter2Fill } from 'react-icons/ri';
import { Button } from '../Button';
import { Appearance, CheckState, Design, Size, Placement } from '../designConstants';
import { InteractiveDiv } from '../InteractiveEl';
import { Menu, MenuRow, useMenuKeyboardNav } from '../Menu';
import { SearchField } from '../SearchField/SearchField';
import { Spinner } from '../Spinner';
import {
  CHECK_STATE_ICONS,
  DropdownFilterPill,
  MenuItem,
  MultiMenuSections,
  SubmenuControlProps,
  SubmenuSelection
} from './dropdown-constants';
import {
  filterSubmenuItems,
  flattenSubmenuItems,
  getSubmenuCheckState,
  getSubmenuItemKey,
  getSubmenuRootItems,
  isMultiMenuItems,
  isSubmenuItemSelected,
  matchesLabelQuery,
  toggleSubmenuItem
} from './dropdown-submenu-utils';

// Emits a filter predicate compatible with `.filter(line => ...)` callers.
// Delegates to the shared search util so dropdowns get tokenized, normalized,
// diacritic-aware matching for free without changing existing consumers.
const buildLabelFilter =
  (query: string) =>
  (line: object): boolean =>
    matchesLabelQuery(line, query);

const getTickIcon = (selected: boolean | undefined, isMultiSelect: boolean): ReactElement => {
  if (isMultiSelect) {
    const CheckboxIcon = CHECK_STATE_ICONS[selected ? CheckState.CHECKED : CheckState.UNCHECKED];
    return <CheckboxIcon />;
  }
  return selected ? <MdCircle /> : <MdOutlineCircle />;
};

const EMPTY_SUBMENU_SELECTION = {};

const convertToMenus = <T extends string>(
  menuItems: MenuItem<T>[] | MenuItem<T>[][]
): MenuItem<T>[][] => {
  let convertedMenus: MenuItem<T>[][];
  if (Array.isArray(menuItems) && isMultiMenuItems(menuItems)) {
    convertedMenus = [...menuItems];
  } else if (Array.isArray(menuItems)) {
    convertedMenus = [menuItems];
  } else {
    convertedMenus = [];
  }
  if (convertedMenus && convertedMenus.length) {
    for (let menuIndex = 0; menuIndex < convertedMenus.length; ++menuIndex) {
      for (let itemIndex = 0; itemIndex < convertedMenus[menuIndex].length; ++itemIndex) {
        const menuItem = convertedMenus[menuIndex][itemIndex];
        if (menuItem.menuIndex == null || menuItem.itemIndex == null) {
          convertedMenus[menuIndex][itemIndex] = {
            ...menuItem,
            menuIndex,
            itemIndex,
            selected: menuItem.selected ?? false,
            label: menuItem.label || menuItem.value
          };
        }
      }
    }
  }
  return convertedMenus;
};

interface DropdownMenuRowContentProps<T> {
  item: MenuItem<T>;
  tick?: ReactElement;
  hasTicks: boolean;
  shouldTruncateOptions?: boolean;
  formatOptionLabel?: (
    option: { label: string; value: string },
    meta?: { context: 'menu' | 'value' }
  ) => ReactNode;
}

const DropdownMenuRowContent = <T,>({
  item,
  tick,
  hasTicks,
  shouldTruncateOptions,
  formatOptionLabel
}: DropdownMenuRowContentProps<T>): ReactElement => {
  const { leadingContent, trailingContent, simpleTooltip, itemOptions, link } = item;
  const label = item.label ?? String(item.value);

  const tickContainer = hasTicks ? <div className='menu-row-tick'>{tick}</div> : null;

  // When a formatOptionLabel is supplied, render its node instead of the plain
  // string label (e.g. the two-line account rows in AccountSelect). Default path
  // (no formatter) is unchanged.
  const labelContent = formatOptionLabel
    ? formatOptionLabel({ label, value: String(item.value) })
    : label;

  const labelClassName = cn('menu-row-label', {
    'menu-row-label--truncated': shouldTruncateOptions
  });
  const labelEl = link ? (
    <a className={labelClassName} href={link}>
      {labelContent}
    </a>
  ) : (
    <div className={labelClassName}>{labelContent}</div>
  );

  // Tooltips wrap the label regardless of disabled state — Menu's onClick guard
  // blocks the action, and the CSS no longer uses pointer-events: none so hover
  // events still fire.
  const labelWithTooltip = simpleTooltip ? (
    <OverlayTrigger placement={Placement.BOTTOM} overlay={<Tooltip>{simpleTooltip}</Tooltip>}>
      {labelEl}
    </OverlayTrigger>
  ) : (
    labelEl
  );

  const itemOptionsContainer = itemOptions ? (
    <div className='menu-row-options' role='presentation' onClick={e => e.stopPropagation()}>
      {itemOptions}
    </div>
  ) : null;

  return (
    <>
      {tickContainer}
      {leadingContent}
      {labelWithTooltip}
      {trailingContent}
      {itemOptionsContainer}
    </>
  );
};

// Submenu mode needs both `submenuValue` and a single-column `menuItems` list with `children`.
export interface DropdownMenuProps<T extends string> extends Partial<SubmenuControlProps<T>> {
  applyContent?: string | ReactElement;
  applyDisabled?: boolean;
  applyIcon?: ReactElement;
  applyOnClick?: ((selectedMenuItems: MenuItem<T>[][]) => void) | null;
  cancelContent?: string | ReactElement;
  cancelIcon?: ReactElement;
  cancelOnClick?: ((selectedMenuItems: MenuItem<T>[][]) => void) | null;
  className?: string;
  // Window long option lists; rows become a fixed height and truncate (see Menu)
  virtualize?: boolean;
  // Opt-in: when sections are present, render a collapse chevron on each section
  // heading. Default (false) leaves every section render path unchanged.
  collapsibleSections?: boolean;
  filterBarPlaceholder?: string | string[];
  filterBarPosition?: 'top' | 'bottom';
  // Opt-in: render a single-select pill row above the list. DropdownMenu owns the
  // active-pill state (uncontrolled) and ANDs the active pill's filter into the
  // filtered-items pipeline. Default undefined => no pill row.
  filterPills?: DropdownFilterPill<T>[];
  footer?: string | ReactElement;
  footerOnClick?: () => void;
  // Opt-in: render a custom node per row in place of the plain string label.
  formatOptionLabel?: (
    option: { label: string; value: string },
    meta?: { context: 'menu' | 'value' }
  ) => ReactNode;
  hasFilterBar?: boolean | boolean[];
  hasTicks?: boolean;
  hideApplyOnClick?: boolean;
  hideCancelOnClick?: boolean;
  id?: string;
  itemOnClick?: (item: MenuItem<T>) => void;
  loading?: boolean;
  menuItems: MenuItem<T>[] | MenuItem<T>[][];
  menuAlign?: ComponentProps<typeof BSDropdown.Menu>['align'];
  // Popper positioning strategy for the menu. Defaults to react-bootstrap's 'absolute', which is
  // clipped by any ancestor with overflow: hidden/auto. Pass 'fixed' to position the menu relative
  // to the viewport so it escapes scroll/modal containers without affecting their layout.
  menuStrategy?: 'absolute' | 'fixed';
  // Opt-in ref on the scrolling items list (.dropdown-menu-items), e.g. for pairing with
  // useIsTruncated to tell when it scrolls. Single-menu dropdowns only: every menu column
  // would share it.
  itemsRef?: Ref<HTMLDivElement>;
  menuRenderOverride?: Record<string, 'stringList'>; // The key is the menu index. The value is the type of render override. Currently only "stringList" is supported.
  menuSectionTitles?: Record<string, ReactElement>; // The titles for each menu in a multi-menu dropdown
  multiMenuSections?: MultiMenuSections; // The sections for each menu in a multi-menu dropdown
  // A single value applies to every menu column; an array is indexed by menu (column)
  // index, letting e.g. a two-column dropdown mix a single-select column with a
  // multi-select one (matching the per-index array convention already used by
  // `hasFilterBar`/`subTitles`/`filterBarPlaceholder` below).
  multiSelect?: boolean | boolean[];
  // Called after a row is clicked in single-select auto-close mode (no apply/cancel
  // buttons, no inline itemOptions). Restores the close-on-select behavior that
  // react-bootstrap's <Dropdown.Item> used to provide before the shared Menu
  // primitive replaced it with plain <li> rows.
  onClose?: () => void;
  onFilter?: (filters: ((line: object) => boolean)[]) => void;
  section?: (string | { title: string; showSectionTitle?: boolean; multiSelect?: boolean })[];
  standalone?: boolean;
  subTitles?: string | string[];
  title?: string;
  shouldTruncateOptions?: boolean;
}

export const DropdownMenu = <T extends string>({
  applyContent = 'Apply',
  applyDisabled = false,
  applyIcon,
  applyOnClick: applyOnClickInput,
  cancelContent = 'Cancel',
  cancelIcon,
  cancelOnClick: cancelOnClickInput,
  className,
  virtualize,
  collapsibleSections = false,
  filterBarPlaceholder: filterBarPlaceholderInput,
  filterBarPosition = 'top',
  filterPills,
  footer: footerInput,
  footerOnClick,
  formatOptionLabel,
  hasFilterBar,
  hasTicks: hasTicksInput,
  hideApplyOnClick,
  hideCancelOnClick,
  id,
  itemOnClick,
  loading,
  menuAlign,
  menuItems: menuItemsInput,
  menuRenderOverride,
  menuStrategy,
  itemsRef,
  menuSectionTitles,
  multiMenuSections,
  multiSelect: multiSelectInput,
  onClose,
  onFilter,
  onSubmenuChange,
  submenuValue,
  section,
  standalone: standaloneInput = false,
  subTitles: subTitlesInput,
  title,
  shouldTruncateOptions = false
}: DropdownMenuProps<T>): ReactElement => {
  // Resolve Different Types of Inputs
  const standalone = standaloneInput;
  const filterBarPlaceholder =
    !filterBarPlaceholderInput || Array.isArray(filterBarPlaceholderInput)
      ? filterBarPlaceholderInput
      : [filterBarPlaceholderInput];
  const isSubmenuControlled = submenuValue != null;
  const submenuRootItems = useMemo(
    () => (isSubmenuControlled ? getSubmenuRootItems(menuItemsInput) : null),
    [isSubmenuControlled, menuItemsInput]
  );
  const submenuSelection: SubmenuSelection<T> = submenuValue ?? EMPTY_SUBMENU_SELECTION;
  const menuItems = useMemo(
    () =>
      submenuRootItems
        ? convertToMenus(
            flattenSubmenuItems(submenuRootItems).map(item => ({
              ...item,
              selected: isSubmenuItemSelected(item, submenuSelection)
            }))
          )
        : convertToMenus(menuItemsInput),
    [menuItemsInput, submenuRootItems, submenuSelection]
  );
  const subTitles =
    !subTitlesInput || Array.isArray(subTitlesInput) ? subTitlesInput : [subTitlesInput];

  // Multi-Menu
  const multiMenu = menuItems.length > 1;

  // Multi-Select. `multiSelectInput` may be a single value shared by every column, or
  // an array indexed by menu (column) index for mixed single/multi-select columns.
  const multiSelectArray = Array.isArray(multiSelectInput) ? multiSelectInput : null;
  const multiSelect: boolean = Array.isArray(multiSelectInput)
    ? multiSelectInput.some(columnValue => columnValue ?? multiMenu)
    : (multiSelectInput ?? (multiMenu || submenuRootItems != null));
  const getMultiSelectForMenu = (menuIndex: number): boolean =>
    multiSelectArray ? (multiSelectArray[menuIndex] ?? multiMenu) : multiSelect;

  // Check if we have any item options
  const hasItemOptions = menuItems.flat().some(menuItem => menuItem.itemOptions != null);

  // If we have multiselect or more than one set of menu items, we need to have ticks.
  const hasTicks =
    hasTicksInput ??
    (applyOnClickInput != null || hasItemOptions || multiMenu || Boolean(multiSelect));

  // If we have a multiple choices or menus, we need to have apply and cancel buttons.
  const needsButtons = hasTicks || multiSelect || multiMenu;

  let applyOnClick = applyOnClickInput;
  if (hideApplyOnClick) {
    applyOnClick = null;
  } else if (needsButtons && applyOnClickInput === undefined) {
    applyOnClick = () => {};
  }

  let cancelOnClick = cancelOnClickInput;
  if (hideCancelOnClick) {
    cancelOnClick = null;
  } else if (needsButtons && cancelOnClickInput === undefined) {
    cancelOnClick = () => {};
  }

  // State Variables
  const [queries, setQueries] = useState<string[]>(() => menuItems.map(() => ''));
  const searchIdBase = useId();

  // Pill filter is uncontrolled (like FilterBar): no pill active by default (show all).
  // The active pill's filter is ANDed with the text filter below.
  const [activePillIndex, setActivePillIndex] = useState<number | undefined>(undefined);
  const activePillFilter =
    activePillIndex !== undefined && filterPills?.[activePillIndex]
      ? filterPills[activePillIndex].filter
      : undefined;

  // Collapsible sections: track collapsed section titles (default expanded => empty set).
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const toggleSection = useCallback((sectionTitle: string) => {
    setCollapsedSections(current => {
      const next = new Set(current);
      if (next.has(sectionTitle)) {
        next.delete(sectionTitle);
      } else {
        next.add(sectionTitle);
      }
      return next;
    });
  }, []);

  const filteredMenuItems = useMemo(
    () =>
      menuItems.map((menuItem, menuIndex) => {
        const query = queries[menuIndex] ?? '';
        if (submenuRootItems) {
          const visibleKeys = new Set(
            flattenSubmenuItems(
              filterSubmenuItems(
                submenuRootItems,
                item =>
                  matchesLabelQuery(item, query) && (!activePillFilter || activePillFilter(item))
              )
            ).map(getSubmenuItemKey)
          );
          return menuItem.filter(item => visibleKeys.has(getSubmenuItemKey(item)));
        }
        const textFiltered = query ? menuItem.filter(buildLabelFilter(query)) : menuItem;
        return activePillFilter ? textFiltered.filter(activePillFilter) : textFiltered;
      }),
    [menuItems, queries, activePillFilter, submenuRootItems]
  );

  const selectedMenuItems = useMemo(
    () => filteredMenuItems.map(menu => menu.filter(item => item.selected === true)),
    [filteredMenuItems]
  );

  const buildMenuRow = useCallback(
    (menuItem: MenuItem<T>, menuItemMultiSelect?: boolean): MenuRow<string> => {
      const { className: itemClassName, selected, disabled, parentValue } = menuItem;

      let tick: ReactElement | undefined;
      if (submenuRootItems) {
        const TickIcon = CHECK_STATE_ICONS[getSubmenuCheckState(menuItem, submenuSelection)];
        tick = <TickIcon />;
      } else if (hasTicks) {
        tick = getTickIcon(menuItem.selected, menuItemMultiSelect ?? false);
      }

      return {
        value: getSubmenuItemKey(menuItem),
        content: (
          <DropdownMenuRowContent
            item={menuItem}
            tick={tick}
            hasTicks={hasTicks}
            formatOptionLabel={formatOptionLabel}
            shouldTruncateOptions={shouldTruncateOptions}
          />
        ),
        selected,
        disabled,
        className: cn(itemClassName, { 'menu-row--submenu-child': parentValue != null })
      };
    },
    [hasTicks, formatOptionLabel, shouldTruncateOptions, submenuRootItems, submenuSelection]
  );

  const handleRowClick = useCallback(
    (menuItem: MenuItem<T>, menuItemMultiSelect: boolean | undefined): void => {
      if (submenuRootItems) {
        onSubmenuChange?.(toggleSubmenuItem(menuItem, submenuSelection));
        itemOnClick?.(menuItem);
        return;
      }
      if (menuItem.menuIndex != null && menuItem.itemIndex != null) {
        const { menuIndex, itemIndex } = menuItem;
        const newMenuItems = [...menuItemsInput] as MenuItem<T>[] | MenuItem<T>[][];
        if (!menuItemMultiSelect) {
          if (multiMenu) {
            for (let i = 0; i < (newMenuItems[menuIndex] as MenuItem<T>[]).length; ++i) {
              if (i !== menuItem.itemIndex) {
                newMenuItems[menuIndex][i].selected = false;
              }
            }
          } else {
            for (let i = 0; i < newMenuItems.length; ++i) {
              if (i !== menuItem.itemIndex) {
                (newMenuItems[i] as MenuItem<T>).selected = false;
              }
            }
          }
        }
        if (multiMenu) {
          newMenuItems[menuIndex][itemIndex].selected =
            !menuItemsInput[menuIndex][itemIndex].selected;
        } else {
          (newMenuItems[itemIndex] as MenuItem<T>).selected = !(
            menuItemsInput[itemIndex] as MenuItem<T>
          ).selected;
        }
      }
      itemOnClick?.(menuItem);
      // Close on select for single-select auto-close mode. apply/cancel buttons own
      // close in multi-select / multi-menu / itemOptions paths (resolved applyOnClick
      // is truthy in those cases), so this only fires for the simple single pick.
      if (!applyOnClick && !cancelOnClick && !menuItem.itemOptions) {
        onClose?.();
      }
    },
    [
      applyOnClick,
      cancelOnClick,
      itemOnClick,
      menuItemsInput,
      multiMenu,
      onClose,
      onSubmenuChange,
      submenuRootItems,
      submenuSelection
    ]
  );

  // Keyboard nav is opt-in for the simple single-select case. Skip it whenever:
  //   - apply/cancel buttons exist (they own Enter as the natural submit target),
  //   - the menu is multi-column (multiMenu),
  //   - or the single-column render path is sectioned (`section.length > 1` or
  //     a populated `multiMenuSections`) — those render paths use multiple <Menu>s
  //     with no shared "current row", and `allRowsForKeyboard` would follow input
  //     order rather than the visual section grouping.
  // We check the *resolved* applyOnClick/cancelOnClick (which auto-default to
  // () => {} for multi-select), not the user-provided props, so a default
  // multi-select dropdown with an implicit Apply button doesn't enable nav.
  const hasMultiMenuSections =
    multiMenuSections != null && Object.keys(multiMenuSections).length > 0;
  const keyboardNavEnabled =
    !applyOnClick &&
    !cancelOnClick &&
    !multiMenu &&
    !(section && section.length > 1) &&
    !hasMultiMenuSections;

  const allRowItems = useMemo(() => filteredMenuItems.flat(), [filteredMenuItems]);
  // Lightweight rows for the keyboard hook — it only reads `value` and `disabled`,
  // so skip building full <DropdownMenuRowContent> trees here. Empty when nav is
  // disabled so the hook becomes a no-op.
  const allRowsForKeyboard = useMemo<MenuRow<string>[]>(
    () =>
      keyboardNavEnabled
        ? allRowItems.map(item => ({
            value: getSubmenuItemKey(item),
            content: null,
            disabled: item.disabled
          }))
        : [],
    [allRowItems, keyboardNavEnabled]
  );
  const itemsByValueAcrossSections = useMemo(
    () => new Map(allRowItems.map(item => [getSubmenuItemKey(item), item])),
    [allRowItems]
  );

  const {
    highlightedIndex,
    setHighlightedIndex,
    onKeyDown: menuKeyDown
  } = useMenuKeyboardNav({
    rows: allRowsForKeyboard,
    onSelect: row => {
      const item = itemsByValueAcrossSections.get(row.value);
      if (item) {
        handleRowClick(item, getMultiSelectForMenu(item.menuIndex ?? 0));
      }
    }
  });

  const renderMenuSection = (
    sectionFilteredMenuItems: MenuItem<T>[][],
    menuIndex: number
  ): ReactElement | null => {
    const items = sectionFilteredMenuItems[menuIndex] ?? [];

    // Skip rendering altogether when the section has no items so we don't
    // surface a stray "No results" row underneath each empty section.
    if (items.length === 0) return null;

    // Map the section's items to the global flat index so the highlight cursor
    // moves through the menu as one continuous list across sections.
    const sectionStart = sectionFilteredMenuItems
      .slice(0, menuIndex)
      .reduce((acc, sectionItems) => acc + sectionItems.length, 0);

    const columnMultiSelect = getMultiSelectForMenu(menuIndex);
    const itemsByValue = new Map(items.map(item => [getSubmenuItemKey(item), item]));
    const rows = items.map(item => buildMenuRow(item, columnMultiSelect));

    const sectionHighlight =
      keyboardNavEnabled &&
      highlightedIndex >= sectionStart &&
      highlightedIndex < sectionStart + items.length
        ? highlightedIndex - sectionStart
        : undefined;

    return (
      <Menu
        rows={rows}
        virtualize={virtualize}
        highlightedIndex={sectionHighlight}
        onHighlightChange={
          keyboardNavEnabled ? i => setHighlightedIndex(sectionStart + i) : undefined
        }
        onRowClick={row => {
          const item = itemsByValue.get(row.value);
          if (item) {
            handleRowClick(item, columnMultiSelect);
          }
        }}
      />
    );
  };

  const menuItemsContainers: ReactElement[] = [];
  for (let menuIndex = 0; menuIndex < menuItems.length; ++menuIndex) {
    const filterBar = ((hasFilterBar == null && menuItems[menuIndex].length >= 10) ||
      hasFilterBar === true ||
      (Array.isArray(hasFilterBar) && hasFilterBar[menuIndex])) && (
      <div className={cn('dropdown-menu-filter-bar-container', filterBarPosition)}>
        <SearchField
          size={Size.LG}
          id={`${searchIdBase}-${menuIndex}`}
          design={Design.NEUTRAL}
          value={queries[menuIndex] ?? ''}
          onChange={e => {
            const nextValue = e.target.value;
            setQueries(currentQueries => {
              const newQueries = [...currentQueries];
              newQueries[menuIndex] = nextValue;
              if (onFilter) {
                onFilter(newQueries.map(q => buildLabelFilter(q)));
              }
              return newQueries;
            });
          }}
          placeholder={filterBarPlaceholder?.[menuIndex] ?? 'Search'}
          aria-label={filterBarPlaceholder?.[menuIndex] ?? 'Search'}
        />
      </div>
    );
    const subTitle = subTitles && subTitles[menuIndex] && (
      <div className='dropdown-menu-items-title-container'>
        <div className='dropdown-menu-items-title'>{subTitles[menuIndex]}</div>
      </div>
    );

    const menuItemsRenderer = () => {
      if (menuRenderOverride && menuRenderOverride[menuIndex]) {
        // If using menu override, we render a string concatenation of the menu items instead of actual checkboxes.
        // The menuIndex tells us which menu "column" to do this for.
        return (
          <div className='dropdown-menu-item-override'>
            {filteredMenuItems[menuIndex].map(item => item.label).join(' + ')}
          </div>
        );
      }
      if (menuItems.length > 0 && filteredMenuItems.length === 0) {
        return (
          <div className='dropdown-menu-item' style={{ cursor: 'default', height: '64px' }}>
            No search results found for &quot;{queries.filter(Boolean).join(' ')}&quot;
          </div>
        );
      }
      if (section && section.length > 1) {
        return section.map((sectionItem, i) => {
          const sectionTitle = typeof sectionItem === 'string' ? sectionItem : sectionItem.title;
          const showSectionTitle =
            typeof sectionItem === 'string' ? true : (sectionItem.showSectionTitle ?? true);
          const sectionMultiSelect =
            typeof sectionItem === 'string'
              ? getMultiSelectForMenu(menuIndex)
              : (sectionItem.multiSelect ?? getMultiSelectForMenu(menuIndex));
          const collapsed = collapsibleSections && collapsedSections.has(sectionTitle);
          const heading = collapsibleSections ? (
            <InteractiveDiv
              className='dropdown-menu-items-title-container collapsible'
              onClick={() => toggleSection(sectionTitle)}
            >
              <span>{sectionTitle}</span>
              <RiArrowUpSFill className={cn('section-chevron', { collapsed })} />
            </InteractiveDiv>
          ) : (
            <div className='dropdown-menu-items-title-container'>{sectionTitle}</div>
          );

          const sectionItems = filteredMenuItems[menuIndex].filter(
            el => el.section === sectionTitle
          );
          if (sectionItems.length === 0) return null;

          const itemsByValue = new Map(sectionItems.map(item => [getSubmenuItemKey(item), item]));
          const rows = sectionItems.map(item => buildMenuRow(item, sectionMultiSelect));

          return (
            <div
              className={cn({ 'dropdown-menu-section': i > 0 })}
              key={`dropdown-menu-section-${i}`}
            >
              {showSectionTitle && heading}
              {!collapsed && (
                <Menu
                  rows={rows}
                  virtualize={virtualize}
                  onRowClick={row => {
                    const item = itemsByValue.get(row.value);
                    if (item) {
                      handleRowClick(item, sectionMultiSelect);
                    }
                  }}
                />
              )}
            </div>
          );
        });
      }
      if (multiMenuSections && multiMenuSections[menuIndex]) {
        const columnMultiSelect = getMultiSelectForMenu(menuIndex);
        return multiMenuSections[menuIndex].map((sectionItem, i) => {
          const { label, displayLabel, footer } = sectionItem;

          const sectionItems = filteredMenuItems[menuIndex].filter(el => el.section === label);
          if (sectionItems.length === 0) return null;

          const itemsByValue = new Map(sectionItems.map(item => [getSubmenuItemKey(item), item]));
          const rows = sectionItems.map(item => buildMenuRow(item, columnMultiSelect));

          return (
            <div
              className={cn({ 'dropdown-menu-section': i > 0 })}
              key={`dropdown-menu-section-${i}`}
            >
              {displayLabel && <div>{label}</div>}
              <Menu
                rows={rows}
                virtualize={virtualize}
                onRowClick={row => {
                  const item = itemsByValue.get(row.value);
                  if (item) {
                    handleRowClick(item, columnMultiSelect);
                  }
                }}
              />
              {footer && <div>{footer}</div>}
            </div>
          );
        });
      }
      return renderMenuSection(filteredMenuItems, menuIndex);
    };

    const menuItemsContainer = (
      <div className='dropdown-menu-items' ref={itemsRef}>
        {menuItemsRenderer()}
      </div>
    );

    // Single global pill row, rendered once above the first menu's items.
    const pillRow = filterPills && filterPills.length > 0 && menuIndex === 0 && (
      <div className='dropdown-menu-pill-row'>
        <RiFilter2Fill className='dropdown-menu-pill-icon' />
        {filterPills.map((pill, pillIndex) => (
          <InteractiveDiv
            key={pill.label}
            className={cn('dropdown-menu-pill', { active: pillIndex === activePillIndex })}
            onClick={() => setActivePillIndex(pillIndex)}
          >
            {pill.label}
          </InteractiveDiv>
        ))}
      </div>
    );

    menuItemsContainers.push(
      <div
        className='dropdown-menu-items-container'
        key={`dropdown-menu-items-container-${menuIndex}`}
      >
        {filterBar}
        {pillRow}
        {subTitle}
        {(menuSectionTitles || {})[menuIndex]}
        {menuItemsContainer}
      </div>
    );
  }

  const header = title && (
    <div className='dropdown-menu-header-container'>
      <div className='dropdown-menu-header-title'>{title}</div>
    </div>
  );

  const body = loading ? (
    <div className='dropdown-menu-spinner'>
      <Spinner />
    </div>
  ) : (
    <div className={cn('dropdown-menu-body-container', { 'multi-menu': multiMenu })}>
      {menuItemsContainers}
    </div>
  );

  const applyCancel = menuItems.flat().length > 0 && (applyOnClick || cancelOnClick) && (
    <div className='dropdown-menu-apply-cancel-container'>
      {cancelOnClick && (
        <BSDropdown.Item>
          <Button
            className='cancel-button'
            onClick={() => cancelOnClick(selectedMenuItems)}
            appearance={Appearance.OUTLINED}
            design={Design.NEUTRAL}
            leadingElement={cancelIcon}
          >
            {cancelContent}
          </Button>
        </BSDropdown.Item>
      )}
      {applyOnClick && (
        <BSDropdown.Item>
          <Button
            className='apply-button'
            onClick={() => applyOnClick(selectedMenuItems)}
            leadingElement={applyIcon}
            disabled={applyDisabled}
          >
            {applyContent}
          </Button>
        </BSDropdown.Item>
      )}
    </div>
  );

  const footer = footerInput && footerOnClick && (
    <InteractiveDiv className='dropdown-menu-footer-container' onClick={footerOnClick}>
      {footerInput}
    </InteractiveDiv>
  );

  return standalone ? (
    <div
      className={cn('dropdown-menu-container', { title: title && title.length }, className)}
      id={id}
      role='menu'
      tabIndex={keyboardNavEnabled ? -1 : undefined}
      onKeyDown={keyboardNavEnabled ? menuKeyDown : undefined}
    >
      {header}
      {body}
      {applyCancel}
      {footer}
    </div>
  ) : (
    <BSDropdown.Menu
      align={menuAlign}
      className={cn('dropdown-menu-container', { title: title && title.length }, className)}
      id={id}
      popperConfig={menuStrategy ? { strategy: menuStrategy } : undefined}
      // Mount the menu up front only for the 'fixed' strategy, whose Popper instance otherwise
      // paints at viewport (0,0) on first open before it can measure the menu element.
      renderOnMount={menuStrategy === 'fixed' ? true : undefined}
      tabIndex={keyboardNavEnabled ? -1 : undefined}
      onKeyDown={keyboardNavEnabled ? menuKeyDown : undefined}
    >
      {header}
      {body}
      {applyCancel}
      {footer}
    </BSDropdown.Menu>
  );
};
